import { useContext, useEffect, useRef, useState, useCallback } from 'react'
import Parser from 'html-react-parser'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { hasPermission, useMixPanel } from 'hooks'
import { getLessons } from 'api'
import { ProjectViewContext } from '../../context/ProjectViewContext'
import { useCurrentUser } from 'context'
import { Text, Stack, LessonsProvider, LessonsList, Paper, Badge, Group, Loader, Center, Button } from 'components'
import { IPagination } from 'interfaces'
import {
  ProjectSection,
  ProjectLearningGoals,
  ProjectEvaluationTools,
  ProjectSkillsStandardsList,
  ProjectFinalAssessment,
} from './components'

// AR-857: right after "Add to my projects" the copied meetings/activities may
// still be materializing in the backend, so the first lessons page can come
// back empty. While that is the case the view re-checks on a fixed cadence,
// with a hard attempt cap so it can never loop requests forever (same numbers
// as useProcessingPoll).
const COPY_POLL_INTERVAL_MS = 4000
const COPY_POLL_MAX_ATTEMPTS = 15

const trimHTML = (html: string | null | undefined): string => {
  if (typeof html !== 'string') return ''
  return html.replace(/<\/?[^>]+(>|$)/g, '')
}

const hasContent = (value: string | null | undefined): boolean =>
  Boolean(value && trimHTML(value) !== '')

export const ProjectViewContentArc = () => {
  const { t } = useTranslation()
  const { currentUser } = useCurrentUser()
  const mixpanel = useMixPanel()
  const observerRef = useRef<HTMLDivElement>(null)

  const {
    project,
    lessons,
    finalAssessmentStarted,
    startFinalAssessmentModal,
    setLessons,
    setSchoolTermEndAt,
    setSchoolTermStartAt,
    schoolTermStartAt,
    schoolTermEndAt,
    lessonsLoading,
    setLessonsLoading,
    finalReportURL,
    setLessonsPagination,
  } = useContext(ProjectViewContext)

  const [pagination, setPagination] = useState<IPagination>({} as IPagination)
  const [page, setPage] = useState<number>(1)
  const [isLoading, setIsLoading] = useState(false)

  const location = useLocation()
  // Set by the "Add to my projects" redirect. A page refresh drops it, which
  // is fine: the poll is only an enhancement for the fresh-copy landing.
  const cameFromTemplateCopy = Boolean(
    (location.state as { arcTemplateCopy?: boolean } | null)?.arcTemplateCopy,
  )
  const [copyPreparing, setCopyPreparing] = useState(cameFromTemplateCopy)
  const [copyTimedOut, setCopyTimedOut] = useState(false)
  const copyAttemptsRef = useRef(0)
  const copyTimerRef = useRef<number>()

  const fetchFirstPage = useCallback(() => {
    setLessons([])
    setPage(1)

    const projectId = project?.id
    if (!projectId) return Promise.resolve(0)

    return getLessons(projectId, 1)
      .then(({ data }) => {
        setLessons(data.sessionMeetings)
        setSchoolTermEndAt(data.schoolTermEndAt)
        setSchoolTermStartAt(data.schoolTermStartAt)
        setPagination(data.pagination)
        setLessonsPagination(data.pagination)
        // AR-887: page 1 is loaded, so the next page to request is 2. Leaving
        // the state at 1 made the first IntersectionObserver trigger refetch
        // page 1 (a wasted request, hidden only by the dedupe below).
        setPage(2)
        return data.sessionMeetings.length as number
      })
      .finally(() => setLessonsLoading(false))
  }, [project?.id, setLessons, setSchoolTermEndAt, setSchoolTermStartAt, setLessonsPagination, setLessonsLoading])

  const scheduleCopyPoll = useCallback(() => {
    copyTimerRef.current = window.setTimeout(() => {
      copyAttemptsRef.current += 1
      fetchFirstPage().then(count => {
        if (count > 0) {
          setCopyPreparing(false)
          return
        }
        if (copyAttemptsRef.current >= COPY_POLL_MAX_ATTEMPTS) {
          setCopyPreparing(false)
          setCopyTimedOut(true)
          return
        }
        scheduleCopyPoll()
      })
    }, COPY_POLL_INTERVAL_MS)
  }, [fetchFirstPage])

  const retryCopyPoll = useCallback(() => {
    setCopyTimedOut(false)
    setCopyPreparing(true)
    copyAttemptsRef.current = 0
    fetchFirstPage().then(count => {
      if (count > 0) {
        setCopyPreparing(false)
        return
      }
      scheduleCopyPoll()
    })
  }, [fetchFirstPage, scheduleCopyPoll])

  const loadMoreItems = useCallback(() => {
    const cannotLoad =
      isLoading ||
      !project?.id ||
      !pagination?.pages ||
      page > pagination.pages

    if (cannotLoad) return

    setIsLoading(true)

    getLessons(project.id, page)
      .then(({ data }) => {
        if (page <= data?.pagination?.pages) {
          const uniqueLessons = [...lessons, ...data.sessionMeetings].reduce(
            (acc: Record<string, any>, lesson: any) => {
              acc[lesson.id] = lesson
              return acc
            },
            {}
          )
          setLessons(Object.values(uniqueLessons))
        }

        setPage(prev => prev + 1)
        setPagination(data.pagination)
        setLessonsPagination(data.pagination)
      })
      .finally(() => setIsLoading(false))
  }, [isLoading, project?.id, pagination?.pages, page, lessons, setLessons, setLessonsPagination])

  useEffect(() => {
    copyAttemptsRef.current = 0
    fetchFirstPage().then(count => {
      if (cameFromTemplateCopy && count === 0) {
        setCopyPreparing(true)
        scheduleCopyPoll()
      } else {
        setCopyPreparing(false)
      }
    })
    return () => window.clearTimeout(copyTimerRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch only when the project changes
  }, [project?.id])

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && !isLoading) {
          loadMoreItems()
        }
      },
      { root: null, rootMargin: '0px', threshold: 1.0 }
    )

    const currentRef = observerRef.current
    if (currentRef) {
      observer.observe(currentRef)
    }

    return () => observer.disconnect()
  }, [loadMoreItems, isLoading])

  if (!project) return null

  const isPublishedTemplate = project.kind === 'template' && project.status === 'published'
  const isEditable = hasPermission(['tutor', 'manager']) && project.status !== 'finished' && !isPublishedTemplate
  const showJournal = project.status === 'approved' && hasPermission(['tutor', 'manager', 'student'])
  const showDivider =
    (project.status === 'finished' && hasPermission(['guardian'])) ||
    (project.status !== 'draft' && hasPermission(['manager', 'student', 'tutor']))
  const skillsCount = project.projectItems.specificSkills.length + project.projectItems.contents.length
  // Arc hides the AI prompt block from students; only teacher profiles see it (AR-535).
  const isStudent = currentUser?.profile_used === 'student'

  return (
    <>
      <Stack spacing={40}>
        {!isStudent && project.userPrompt && (
          <Paper p="md" radius="md" sx={(theme) => ({
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.grape[0],
            border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.grape[2]}`,
          })}>
            <Text weight={600} size="sm" mb="xs">{t('new_project_view.original_prompt_title')}</Text>
            <Text size="sm" sx={{ lineHeight: 1.6 }} mb={project.areas?.length ? 'md' : 0}>{project.userPrompt}</Text>
            {project.areas && project.areas.length > 0 && (
              <>
                <Text weight={600} size="sm" mb="xs">{t('new_project_view.chosen_topics')}</Text>
                <Group spacing="xs">
                  {project.areas.map((area) => (
                    <Badge key={area.id} variant="filled" color="grape" size="sm">
                      {area.name}
                    </Badge>
                  ))}
                </Group>
              </>
            )}
          </Paper>
        )}

        {hasContent(project.purpose) && (
          <ProjectSection title={t('new_project_view.title_driving_question')}>
            <Text size="md" sx={{ lineHeight: 1.6 }}>{project.purpose}</Text>
          </ProjectSection>
        )}

        {hasContent(project.synopsis) && (
          <ProjectSection title={t('new_project_view.title_summary')}>
            <Text size="md" sx={{ lineHeight: 1.6 }}>{project.synopsis}</Text>
          </ProjectSection>
        )}

        {hasContent(project.objectives) && (
          <ProjectSection title={t('new_project_view.title_learning_goals')}>
            <ProjectLearningGoals objectives={project.objectives} />
          </ProjectSection>
        )}

        {project.evaluationTools?.length > 0 && (
          <ProjectSection title={t('new_project_page.evaluation_tools')}>
            <ProjectEvaluationTools tools={project.evaluationTools} />
          </ProjectSection>
        )}

        {hasContent(project.finalProductDescription) && (
          <ProjectSection title={t('new_project_view.title_deliverable')}>
            <Text size="md" sx={{ lineHeight: 1.6 }}>{Parser(project.finalProductDescription)}</Text>
          </ProjectSection>
        )}

        <ProjectSection title={t('new_project_view.title_worked_skills_standards', { count: skillsCount })}>
          <ProjectSkillsStandardsList
            specificSkills={project.projectItems.specificSkills}
            contents={project.projectItems.contents}
            showCyclesBadges={false}
          />
        </ProjectSection>

        <Stack spacing={0} mt={8}>
          {copyPreparing || copyTimedOut ? (
            // HIG: indeterminate progress with a plain-language explanation of
            // what is happening, and a recovery action instead of an endless
            // spinner once the attempt cap is reached
            <Paper withBorder radius='md' p='xl'>
              <Stack align='center' spacing='xs'>
                {copyPreparing && <Loader size='sm' color='teal' />}
                <Text size='sm' weight={600} align='center'>
                  {t(copyTimedOut
                    ? 'new_project_view.preparing_lessons_timeout_title'
                    : 'new_project_view.preparing_lessons_title')}
                </Text>
                <Text size='sm' color='dimmed' align='center'>
                  {t(copyTimedOut
                    ? 'new_project_view.preparing_lessons_timeout_description'
                    : 'new_project_view.preparing_lessons_description')}
                </Text>
                {copyTimedOut && (
                  <Button variant='default' mt='xs' onClick={retryCopyPoll}>
                    {t('new_project_view.preparing_lessons_check_again')}
                  </Button>
                )}
              </Stack>
            </Paper>
          ) : (
            <LessonsProvider>
              <LessonsList
                kind={project.kind}
                showLessonTitle
                projectID={project.id}
                projectStatus={project.status}
                editable={isEditable}
                lessonsList={lessons}
                journal={showJournal}
                loading={lessonsLoading}
                schoolTermEndAt={schoolTermEndAt}
                schoolTermStartAt={schoolTermStartAt}
                fetchAndSetActivities={fetchFirstPage}
                selectedSkills={project.projectItems.specificSkills}
                selectedContents={project.projectItems.contents}
                lessonsPagination={pagination}
                isView
              />
            </LessonsProvider>
          )}
        </Stack>

        {showDivider && (
          <ProjectFinalAssessment
            projectId={project.id}
            projectName={project.name}
            projectStatus={project.status}
            projectKind={project.kind}
            finalAssessmentStarted={finalAssessmentStarted}
            startFinalAssessmentModal={startFinalAssessmentModal}
            finalReportURL={finalReportURL}
            onFinalReportClick={() => mixpanel.track('Ver relatório final do projeto')}
          />
        )}

        {isLoading && (
          <Center py='md'>
            <Loader size='sm' />
          </Center>
        )}
        <div ref={observerRef} />
      </Stack>
    </>
  )
}
